/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { RoomData } from '../sync/interface';
import type { IIndexedDBBackend, UserTuple } from './interfaces/indexeddb-backend';
import type { IStoredClientOpts } from './interfaces/store';

import { logger } from '../utils/logger';

export class RemoteIndexedDBStoreBackend implements IIndexedDBBackend {
  /**
   * Service worker for handling IndexedDB operations in a separate thread.
   */
  private worker?: Worker;
  private nextSeq = 0;

  // Once we start connecting, we keep the promise and re-use it
  // if we try to connect again
  private startPromise?: Promise<void>;
  // The currently in-flight requests to the actual backend
  // Requests from main thread to worker, but not yet replied to (pending)
  private inFlight: Record<number, PromiseWithResolvers<any>> = {}; // seq: promise

  /**
   * Callback when the database connection is closed.
   */
  private onClose?(): void;

  /**
   * An IndexedDB store backend where the actual backend sits in a web
   * worker.
   *
   * Construct a new Indexed Database store backend. This requires a call to
   * `connect()` before this store can be used.
   * @param workerFactory - Factory which produces a Worker
   * @param dbName - Optional database name. The same name must be used
   * to open the same database.
   * @param userId - Optional user ID for database isolation.
   */
  public constructor(
    private readonly workerFactory: () => Worker,
    private readonly dbName?: string,
    private readonly userId?: string,
  ) {}

  /**
   * Clear the entire database. This should be used when logging out of a client
   * to prevent mixing data between accounts.
   * @returns Resolved when the database is cleared.
   */
  public async clearDatabase(): Promise<void> {
    return this.ensureStarted().then(() => this.doCmd('clearDatabase'));
  }

  /** @returns whether or not the database was newly created in this session. */
  public isNewlyCreated(): Promise<boolean> {
    return this.doCmd('isNewlyCreated');
  }

  /**
   * Attempt to connect to the database. This can fail if the user does not
   * grant permission.
   * @returns Promise which resolves if successfully connected.
   */
  public async connect(onClose?: () => void): Promise<void> {
    this.onClose = onClose;
    return this.ensureStarted().then(() => {
      this.doCmd('connect');
    });
  }

  public storeClientOptions(options: IStoredClientOpts): Promise<void> {
    return this.doCmd('storeClientOptions', [options]);
  }

  public getClientOptions(): Promise<IStoredClientOpts | undefined> {
    return this.doCmd('getClientOptions');
  }

  /**
   * Load all user presence events from the database. This is not cached.
   * @returns A list of presence events in their raw form.
   */
  public getUserPresenceEvents(): Promise<UserTuple[]> {
    return this.doCmd('getUserPresenceEvents');
  }

  public syncToDatabase(userTuples: UserTuple[]): Promise<void> {
    return this.doCmd('syncToDatabase', [userTuples]);
  }

  public getSavedRooms(copy = true): Promise<RoomData[] | null> {
    return this.doCmd('getSavedRooms', [copy]);
  }

  public setRoomsData(roomsData: Record<string, RoomData>): Promise<void> {
    return this.doCmd('setRoomsData', [roomsData]);
  }

  /**
   * ======================================================================
   *                            UTIL METHODS
   * ======================================================================
   */

  private ensureStarted(): Promise<void> {
    if (!this.startPromise) {
      this.worker = this.workerFactory();
      this.worker.onmessage = this.onWorkerMessage;

      // tell the worker the db name and userId for isolation
      this.startPromise = this.doCmd('setupWorker', [this.dbName, this.userId]).then(() => {
        logger.info('IndexedDB worker is ready');
      });
    }
    return this.startPromise;
  }

  private onWorkerMessage = (ev: MessageEvent): void => {
    const msg = ev.data;

    if (msg.command === 'closed') {
      this.onClose?.();
    } else if (msg.command === 'cmd_success' || msg.command === 'cmd_fail') {
      if (msg.seq === undefined) {
        logger.error('Got reply from worker with no seq');
        return;
      }

      const def = this.inFlight[msg.seq];
      if (def === undefined) {
        logger.error(`Got reply for unknown seq ${msg.seq}`);
        return;
      }
      delete this.inFlight[msg.seq];

      if (msg.command === 'cmd_success') {
        def.resolve(msg.result);
      } else {
        const error = new Error(msg.error.message);
        error.name = msg.error.name;
        def.reject(error);
      }
    } else {
      logger.warn('Unrecognized message from worker: ', msg);
    }
  };

  /**
   * Perform a command against the backend.
   * @param command - Command name
   * @param args - Command arguments
   * @returns Promise which resolves with the command result
   */
  private async doCmd<T>(command: string, args?: any): Promise<T> {
    // wrap in a q so if the postMessage throws,
    // the promise automatically gets rejected
    return Promise.resolve().then(() => {
      const seq = this.nextSeq++;
      const def = Promise.withResolvers<T>();

      this.inFlight[seq] = def;

      this.worker?.postMessage({ command, seq, args });

      return def.promise;
    });
  }

  /**
   * Destroy the web worker
   */
  public async destroy(): Promise<void> {
    this.worker?.terminate();
  }
}
